/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-13 10:21:40
 *
 *******************************************************/
//flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, FormLayout, Link } from '@shopify/polaris';
import { Field, reduxForm, reset } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import * as merchandisingAction from 'actions/merchandising-actions';
import { isRequired, hasTag, isExceedLength } from 'src/helpers/form-validation-helper';
import { prettyText } from 'src/helpers';

const isRequiredValue = isRequired();
const isExceedLengthValue = isExceedLength(10000);

type Props = {
  feedbackVersion: boolean,
  handleSubmit: Function,
  sendFeedback: () => void
};

type ComposedProps = Props & WithI18nProps;

class SwitchVersionFeedbackModal extends Component<ComposedProps> {
  render() {
    const { feedbackVersion, handleSubmit, submitting, pristine, i18n, loading } = this.props;

    return (
      <Modal
        open={feedbackVersion}
        onClose={this.handleClose}
        title={i18n.translate('AdminHTML.Merchandising.SwitchVersionFeedbackModal.title')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.sendFeedback'),
          disabled: pristine || submitting,
          loading: loading && !pristine,
          onAction: handleSubmit(this.handleSendRequest.bind(this))
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleClose
          }
        ]}>
        <Modal.Section>
          <FormLayout>
            <p>
              {i18n.translate('AdminHTML.Merchandising.SwitchVersionFeedbackModal.paragraph01')}
            </p>
            <Field
              name="content"
              component={RenderTextField}
              placeholder={i18n.translate(
                'AdminHTML.Merchandising.SwitchVersionFeedbackModal.content_Placeholder'
              )}
              anotherProps={{ multiline: 3 }}
              validate={[isRequiredValue, hasTag, isExceedLengthValue]}
            />
            <p>
              {i18n.translate('AdminHTML.Merchandising.SwitchVersionFeedbackModal.paragraph02', {
                t1: (
                  <Link external url={URL['MerchandisingDoc']}>
                    {i18n.translate(
                      'AdminHTML.Merchandising.SwitchVersionFeedbackModal.paragraph02_t1'
                    )}
                  </Link>
                )
              })}
            </p>
          </FormLayout>
        </Modal.Section>
      </Modal>
    );
  }

  handleClose = () => {
    this.props.resetForm();
    this.props.hideFeedbackVersion();
  };

  handleSendRequest = data => {
    const { sendFeedback } = this.props;
    const prettyData = {
      content: prettyText(data.content),
      note: 'Switch back to classic version'
    };
    const meta = {
      onSuccess: () => {
        this.handleClose();
      }
    };
    sendFeedback(prettyData, meta);
  };
}

const mapStateToProps = (state, props) => ({
  initialValue: {},
  loading: _.get(state.merchandising, 'feedbackLoading', false),
  feedbackVersion: _.get(state.merchandising, 'feedbackVersion', false)
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendFeedback: (body: Object, meta: Object) =>
    dispatch(merchandisingAction.sendFeedback(body, meta, ownProps.i18n)),
  hideFeedbackVersion: () => dispatch(merchandisingAction.hideFeedbackVersion()),
  resetForm: () => dispatch(reset('SwitchVersionMerchandisingFeedback'))
});

const SwitchVersionFeedbackModalForm = reduxForm({
  form: 'SwitchVersionMerchandisingFeedback', // a unique identifier for this form
  enableReinitialize: true
})(SwitchVersionFeedbackModal);

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(SwitchVersionFeedbackModalForm)
);
